import { flushSync } from 'react-dom';

export type StageTransitionType =
  | 'scene-morph'
  | 'card-expand'
  | 'card-collapse'
  | 'forward'
  | 'backward';

let activeTransitionSeq = 0;

function routeAccessibilityFocus(targetSelector = '[data-stage-heading="true"]'): void {
  if (typeof document === 'undefined') return;
  const runFocus = () => {
    const heading =
      (document.getElementById('stage-active-heading') as HTMLElement | null) ||
      (document.querySelector(targetSelector) as HTMLElement | null);
    if (heading && typeof heading.focus === 'function') {
      if (!heading.hasAttribute('tabindex')) {
        heading.setAttribute('tabindex', '-1');
      }
      heading.focus({ preventScroll: true });
    }
  };
  if (typeof requestAnimationFrame === 'function') {
    requestAnimationFrame(runFocus);
  } else {
    runFocus();
  }
}

/**
 * Executes a React state transition inside the W3C View Transitions API
 * (`document.startViewTransition` + `flushSync`) with Apple/Google Material 3
 * Expressive out -> in choreography and mandatory accessibility focus routing.
 */
export function runStageViewTransition(
  updateDOM: () => void,
  transitionType: StageTransitionType = 'scene-morph'
): void {
  const mySeq = ++activeTransitionSeq;

  if (typeof document === 'undefined' || typeof window === 'undefined') {
    updateDOM();
    return;
  }

  const prefersReducedMotion =
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const isMobileOrCoarse =
    (typeof window.innerWidth === 'number' && window.innerWidth <= 900) ||
    (typeof window.matchMedia === 'function' &&
      window.matchMedia('(max-width: 900px), (hover: none) and (pointer: coarse)').matches);

  const docAny = document as any;
  if (prefersReducedMotion || isMobileOrCoarse || typeof docAny.startViewTransition !== 'function') {
    updateDOM();
    routeAccessibilityFocus();
    return;
  }

  const rootEl = document.documentElement;
  rootEl.setAttribute('data-transition-type', transitionType);

  const performSyncUpdate = () => {
    try {
      flushSync(() => {
        updateDOM();
      });
    } catch {
      updateDOM();
    }
  };

  try {
    let transition: any;
    try {
      // Modern Level 2 signature supporting :active-view-transition-type()
      transition = docAny.startViewTransition({
        update: performSyncUpdate,
        types: [transitionType]
      });
    } catch {
      // Level 1 signature taking callback directly
      transition = docAny.startViewTransition(performSyncUpdate);
    }

    if (transition && transition.finished && typeof transition.finished.finally === 'function') {
      transition.finished
        .catch(() => {})
        .finally(() => {
          // Only clean up attribute and route focus if no newer transition superseded this one
          if (mySeq === activeTransitionSeq) {
            if (rootEl.getAttribute('data-transition-type') === transitionType) {
              rootEl.removeAttribute('data-transition-type');
            }
            routeAccessibilityFocus();
          }
        });
    } else if (mySeq === activeTransitionSeq) {
      rootEl.removeAttribute('data-transition-type');
      routeAccessibilityFocus();
    }
  } catch {
    if (mySeq === activeTransitionSeq) {
      rootEl.removeAttribute('data-transition-type');
    }
    updateDOM();
    if (mySeq === activeTransitionSeq) {
      routeAccessibilityFocus();
    }
  }
}

